import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChefHat, Calendar, Award, Users } from 'lucide-react';

const Home = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const features = [
    {
      icon: <ChefHat size={40} />,
      title: 'Expert Catering',
      description: 'Specializing in American, Southern, and Caribbean cuisine with authentic flavors.',
      color: 'from-[#ff6b6b] to-[#ee5a6f]'
    },
    {
      icon: <Calendar size={40} />,
      title: 'Event Planning',
      description: 'Full-service event planning to make your special occasion unforgettable.',
      color: 'from-[#4ecdc4] to-[#44a08d]'
    },
    {
      icon: <Award size={40} />,
      title: 'Quality Service',
      description: 'Years of experience delivering exceptional service and delicious food.',
      color: 'from-[#ffd93d] to-[#f7b731]'
    },
    {
      icon: <Users size={40} />,
      title: 'Any Event Size',
      description: 'From intimate gatherings to large celebrations, we handle it all.',
      color: 'from-[#a29bfe] to-[#6c5ce7]'
    },
  ];

  return (
    <div className="pt-20">
      {/* Hero Section with Background Image */}
      <section 
        className="relative min-h-[90vh] flex items-center justify-center overflow-hidden" 
        data-testid="home-hero-section"
        style={{
          backgroundImage: 'linear-gradient(rgba(0, 171, 181, 0.85), rgba(168, 224, 227, 0.85)), url(https://images.unsplash.com/photo-1555244162-803834f70033?w=1600&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="hero-content fade-in-up relative z-10">
          <h1 className="hero-title text-white drop-shadow-lg" data-testid="home-hero-title">
            JFRAI Family Catering
            <br />
            & Event Planning
          </h1>
          <div className="decorative-line bg-white"></div>
          <p className="hero-subtitle text-white drop-shadow-md" data-testid="home-hero-subtitle">
            Creating Unforgettable Experiences Through Exceptional Food and Service
          </p>
          <div className="flex flex-wrap justify-center gap-4 mt-8">
            <Link to="/catering">
              <button className="btn-primary" data-testid="home-view-catering-btn">
                View Catering Menu
              </button>
            </Link>
            <Link to="/events">
              <button className="btn-secondary bg-white text-[#0abab5] hover:bg-[#0abab5] hover:text-white border-white" data-testid="home-plan-event-btn">
                Plan Your Event
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section" style={{ background: '#ffe5d9' }} data-testid="home-features-section">
        <h2 className="section-title">Why Choose JFRAI?</h2>
        <div className="decorative-line"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
          {features.map((feature, index) => (
            <div
              key={index}
              className="card text-center overflow-hidden relative group"
              data-testid={`home-feature-card-${index}`}
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}></div>
              <div className="flex justify-center mb-4 text-white relative z-10">
                <div className={`p-4 rounded-full bg-gradient-to-br ${feature.color}`}>
                  {feature.icon}
                </div>
              </div>
              <h3 className="text-xl font-bold mb-3 text-[#8b4513] relative z-10">{feature.title}</h3>
              <p className="text-[#6b4423] relative z-10">{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Cuisine Section with Images */}
      <section className="section" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }} data-testid="home-cuisine-section">
        <h2 className="section-title text-white">Our Specialties</h2>
        <div className="decorative-line bg-white"></div>
        <p className="section-subtitle text-white">
          We bring together the best of three culinary traditions
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-8">
          <div className="card group cursor-pointer" data-testid="home-cuisine-american">
            <div className="h-48 overflow-hidden rounded-lg mb-4">
              <img 
                src="https://images.unsplash.com/photo-1544025162-d76694265947?w=600&q=80" 
                alt="American Cuisine BBQ Ribs"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
              />
            </div>
            <h3 className="text-2xl font-bold mb-3 text-[#8b4513]">American</h3>
            <p className="text-[#6b4423]">
              Classic comfort foods and modern favorites that bring people together.
            </p>
          </div>
          <div className="card group cursor-pointer" data-testid="home-cuisine-southern">
            <div className="h-48 overflow-hidden rounded-lg mb-4">
              <img 
                src="https://images.unsplash.com/photo-1626645738196-c2a7c87a8f58?w=600&q=80" 
                alt="Southern Fried Chicken"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
              />
            </div>
            <h3 className="text-2xl font-bold mb-3 text-[#8b4513]">Southern</h3>
            <p className="text-[#6b4423]">
              Soul-warming dishes with authentic Southern hospitality and flavor.
            </p>
          </div>
          <div className="card group cursor-pointer" data-testid="home-cuisine-caribbean">
            <div className="h-48 overflow-hidden rounded-lg mb-4">
              <img 
                src="https://images.unsplash.com/photo-1658833608786-22c4b4a621de?w=600&q=80" 
                alt="Caribbean Jerk Chicken"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
              />
            </div>
            <h3 className="text-2xl font-bold mb-3 text-[#8b4513]">Caribbean</h3>
            <p className="text-[#6b4423]">
              Vibrant, tropical flavors that transport you to island paradise.
            </p>
          </div>
        </div>
      </section>

      {/* Testimonials with Photos */}
      <section className="section" style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }} data-testid="home-testimonials-section">
        <h2 className="section-title text-white">Happy Clients</h2>
        <div className="decorative-line bg-white"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-12">
          <div className="card">
            <div className="h-64 overflow-hidden rounded-lg mb-4">
              <img 
                src="https://images.unsplash.com/photo-1528605248644-14dd04022da1?w=800&q=80" 
                alt="Happy clients enjoying food"
                className="w-full h-full object-cover"
              />
            </div>
            <p className="text-[#6b4423] italic mb-4">
              "The food was absolutely incredible! Everyone at our wedding is still talking about the amazing Caribbean dishes."
            </p>
            <p className="text-[#8b4513] font-bold">- Sarah & Michael</p>
          </div>
          <div className="card">
            <div className="h-64 overflow-hidden rounded-lg mb-4">
              <img 
                src="https://images.pexels.com/photos/5738306/pexels-photo-5738306.jpeg?w=800&q=80" 
                alt="Corporate event"
                className="w-full h-full object-cover"
              />
            </div>
            <p className="text-[#6b4423] italic mb-4">
              "JFRAI made our corporate event a huge success. Professional service and outstanding food quality!"
            </p>
            <p className="text-[#8b4513] font-bold">- TechCorp Inc.</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section text-center" style={{ background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)' }} data-testid="home-cta-section">
        <h2 className="section-title">Ready to Get Started?</h2>
        <div className="decorative-line"></div>
        <p className="section-subtitle">
          Contact us today for a free quote and let's make your event extraordinary
        </p>
        <Link to="/contact">
          <button className="btn-primary" data-testid="home-contact-btn">
            Get in Touch
          </button>
        </Link>
      </section>
    </div>
  );
};

export default Home;